import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { Board, Move, IPiece, Position, PieceType } from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 9: Two Turn Race
 *
 * This priority SOLELY looks for UNSTOPPABLE 2-turn race victories!
 * It does not look for general advancement or free captures!
 * It does not look for king safety or defensive moves!
 * It does not look for trade opportunities!
 * It does not look for piece specific heuristics!
 * It does not look for strategic advancement!
 * It does not look for back-up support!
 *
 * CRITICAL FIXES APPLIED:
 * 1. PURE SIMULATION: No distance calculations - uses only simulation to determine feasibility
 * 2. Checks unfiltered moves first to bypass defensive filtering
 * 3. Simplified and fixed simulation logic
 * 4. Fixed enemy piece detection
 * 5. Concise logging for performance
 *
 * It has one priority, if there is an unstoppable 2-turn race victory, take it immediately!
 * This means after our move, regardless of enemy response, we can win on our next turn.
 *
 * Uses pure simulation to test each king move:
 * 1. Simulate our king move
 * 2. For each possible enemy response, simulate their move
 * 3. Check if our king can still reach victory after each enemy response
 * 4. Only early exit if king can win regardless of ALL enemy responses
 */
export class TwoTurnRacePriority extends BaseDecisionPriority {
  readonly priority = 9;
  readonly name = "Two Turn Race";
  readonly description =
    "CRITICAL: Execute ONLY genuine 2-turn race victories (verified by 3-ply simulation) - NO general advancement";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // CONSERVATIVE: Only relevant if there's actually a potential 2-turn race
    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const ourVictoryRow = isPlayerTurn ? 7 : 0;

    // Find our king first
    const ourKing = this.findOurKing(gameState, isPlayerTurn);
    if (!ourKing) return false;

    // Get king moves available
    const kingMoves = context.allPossibleMoves.filter((move) => {
      const piece = MCTSUtils_v2.getPieceAt(gameState.board, move.fromPosition);
      return piece && piece.isKing;
    });

    if (kingMoves.length === 0) {
      return false; // No king moves available
    }

    // SIMULATION-BASED CHECK: Can any king move lead to victory in 2 turns?
    for (const kingMove of kingMoves) {
      // Simulate our king move
      const stateAfterOurMove = cloneFullGameState(gameState);
      const ourPiece = getPieceFromPosition(
        stateAfterOurMove,
        kingMove.fromPosition
      );

      if (!ourPiece) continue;

      try {
        // Apply our move
        applyMoveWithSnakeRespawn(
          stateAfterOurMove,
          kingMove.fromPosition,
          kingMove.move,
          ourPiece
        );

        // Check if we're already at victory after our move
        if (kingMove.move.position.row === ourVictoryRow) {
          return true; // Can win in 1 move, so definitely relevant
        }

        // Switch to enemy turn
        stateAfterOurMove.isLightTurn = !stateAfterOurMove.isLightTurn;

        // Generate our moves after any enemy response
        const ourMovesAfterEnemyTurn =
          MCTSMoveGenerationService.getInstance().getAllMoves(
            stateAfterOurMove
          );

        // Check if any of our follow-up moves can reach victory
        for (const ourSecondMove of ourMovesAfterEnemyTurn) {
          const secondMovePiece = MCTSUtils_v2.getPieceAt(
            stateAfterOurMove.board,
            ourSecondMove.fromPosition
          );

          if (
            secondMovePiece &&
            secondMovePiece.isKing &&
            secondMovePiece.isLight === ourPiece.isLight
          ) {
            // Check if this second move reaches victory row
            if (ourSecondMove.move.position.row === ourVictoryRow) {
              return true; // Found potential 2-turn race
            }
          }
        }
      } catch (error) {
        continue; // Skip invalid moves
      }
    }

    return false; // No viable 2-turn race opportunities found through simulation
  }

  analyze(context: DecisionContext): DecisionResult {
    // Initialize with minimal logging for performance
    this.analysisLogs = [];

    let gameState: any;
    let isPlayerTurn: boolean;
    let ourVictoryRow: number;

    try {
      gameState = context.gameState;
      isPlayerTurn = gameState.isLightTurn;
      ourVictoryRow = isPlayerTurn ? 7 : 0;
    } catch (error) {
      this.addLog(`❌ Exception: ${error}`);
      return {
        shouldEarlyExit: false,
        reasoning: `Early exception: ${error}`,
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Find our king first
    let ourKing;
    try {
      ourKing = this.findOurKing(gameState, isPlayerTurn);
    } catch (error) {
      this.addLog(`❌ King finding exception: ${error}`);
      return {
        shouldEarlyExit: false,
        reasoning: `King finding exception: ${error}`,
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    if (!ourKing) {
      this.addLog("❌ No king found");
      return {
        shouldEarlyExit: false,
        reasoning: "No king found",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // RESPECT PRIORITY HIERARCHY: Only check moves approved by higher priorities
    const filteredKingMoves = context.allPossibleMoves.filter((move) => {
      const piece = MCTSUtils_v2.getPieceAt(gameState.board, move.fromPosition);
      return piece && piece.isKing;
    });

    this.addLog(
      `🎯 Checking ${filteredKingMoves.length} king moves approved by higher priorities`
    );

    // Check only the filtered (safe) moves - respect Priority 6 King Safety filtering
    const raceResult = this.checkMovesForRaceVictories(
      context.allPossibleMoves,
      gameState,
      ourVictoryRow,
      "APPROVED SAFE MOVES"
    );
    if (raceResult.shouldEarlyExit) {
      return raceResult;
    }

    this.addLog("❌ No unstoppable 2-turn race victories found");
    return {
      shouldEarlyExit: false,
      reasoning: "No genuine 2-turn race victories detected",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private checkMovesForRaceVictories(
    movesToAnalyze: MoveInfo[],
    gameState: any,
    ourVictoryRow: number,
    moveType: string
  ): DecisionResult {
    let kingMovesChecked = 0;
    let validTwoTurnRaces: Array<{
      moveInfo: MoveInfo;
      piece: IPiece;
      analysis: any;
    }> = [];

    this.addLog(
      `🔍 ${moveType}: Analyzing ${movesToAnalyze.length} moves for race potential`
    );

    // Find and analyze king moves with detailed logging for debugging
    for (const moveInfo of movesToAnalyze) {
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );
      if (!piece || !piece.isKing) continue;

      kingMovesChecked++;

      this.addLog(
        `   ${kingMovesChecked}. ${piece.type} [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`
      );

      const twoTurnAnalysis = this.simulate3PlyForKingRace(
        moveInfo,
        gameState,
        ourVictoryRow
      );

      this.addLog(`      Result: ${twoTurnAnalysis.description}`);

      if (twoTurnAnalysis.canWinInTwoTurns) {
        this.addLog(`      ✅ VALID 2-turn race found!`);
        validTwoTurnRaces.push({
          moveInfo,
          piece,
          analysis: twoTurnAnalysis,
        });
      } else {
        this.addLog(`      ❌ Rejected: ${twoTurnAnalysis.description}`);
      }
    }

    this.addLog(
      `📊 ${moveType} Summary: ${kingMovesChecked} king moves tested, ${validTwoTurnRaces.length} valid races found`
    );

    // If we found valid 2-turn races, prioritize immediate race setups over regular 2-turn races
    if (validTwoTurnRaces.length > 0) {
      // Separate immediate race setups (1 move from victory) from regular 2-turn races using simulation
      const immediateRaceSetups = validTwoTurnRaces.filter((race) =>
        this.canReachVictoryInOneMove(
          race.moveInfo.move.position.row,
          ourVictoryRow
        )
      );

      const chosenRace =
        immediateRaceSetups.length > 0
          ? immediateRaceSetups[0] // Prioritize immediate race setup
          : validTwoTurnRaces[0]; // Fall back to first 2-turn race

      const raceType =
        immediateRaceSetups.length > 0 ? "immediate race setup" : "2-turn race";

      this.addLog(
        `✅ ${moveType} ${raceType}: ${chosenRace.piece.type} [${chosenRace.moveInfo.fromPosition.row},${chosenRace.moveInfo.fromPosition.col}] → [${chosenRace.moveInfo.move.position.row},${chosenRace.moveInfo.move.position.col}]`
      );

      return this.createEarlyExitResult(
        chosenRace.moveInfo,
        `2-turn race victory: ${chosenRace.piece.type}`,
        {
          kingRaceSetup: true,
          kingPiece: chosenRace.piece.type,
          selectedMove: `[${chosenRace.moveInfo.fromPosition.row},${chosenRace.moveInfo.fromPosition.col}] -> [${chosenRace.moveInfo.move.position.row},${chosenRace.moveInfo.move.position.col}]`,
          totalRaces: validTwoTurnRaces.length,
          logs: this.analysisLogs,
        }
      );
    }

    return {
      shouldEarlyExit: false,
      reasoning: `No 2-turn race found in ${moveType} moves`,
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private canReachVictoryInOneMove(
    currentRow: number,
    victoryRow: number
  ): boolean {
    // Binary logic: Can a king at currentRow reach victoryRow in one move?
    // King pieces can move 1 square in any direction
    const rowDistance = Math.abs(currentRow - victoryRow);
    return rowDistance === 1;
  }

  private findOurKing(
    gameState: any,
    isLightTurn: boolean
  ): {
    piece: IPiece;
    position: Position;
  } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isKing && piece.isLight === isLightTurn) {
          return {
            piece,
            position: { row, col },
          };
        }
      }
    }
    return null;
  }

  private simulate3PlyForKingRace(
    ourMove: MoveInfo,
    gameState: any,
    ourVictoryRow: number
  ): {
    canWinInTwoTurns: boolean;
    description: string;
  } {
    // PURE SIMULATION: No distance calculations - only simulation determines feasibility

    // Simulate our king move
    const stateAfterOurMove = cloneFullGameState(gameState);
    const ourPiece = getPieceFromPosition(
      stateAfterOurMove,
      ourMove.fromPosition
    );

    if (!ourPiece) {
      return {
        canWinInTwoTurns: false,
        description: "Invalid piece for our move",
      };
    }

    // Apply our move using engine's standard move application
    try {
      applyMoveWithSnakeRespawn(
        stateAfterOurMove,
        ourMove.fromPosition,
        ourMove.move,
        ourPiece
      );
    } catch (error) {
      return {
        canWinInTwoTurns: false,
        description: `Move simulation failed: ${error}`,
      };
    }

    // Switch turn to enemy
    stateAfterOurMove.isLightTurn = !stateAfterOurMove.isLightTurn;

    // Find where our king ended up
    const kingNewPosition = ourMove.move.position;
    const movedKing = MCTSUtils_v2.getPieceAt(
      stateAfterOurMove.board,
      kingNewPosition
    );
    if (!movedKing || !movedKing.isKing) {
      return {
        canWinInTwoTurns: false,
        description: "King not found at expected position after move",
      };
    }

    // Generate all possible enemy responses
    const enemyMoves =
      MCTSMoveGenerationService.getInstance().getAllMoves(stateAfterOurMove);

    // For each enemy response, check if we can still win on our next turn
    for (const enemyMove of enemyMoves) {
      // Simulate enemy move
      const stateAfterEnemyMove = cloneFullGameState(stateAfterOurMove);
      const enemyPiece = getPieceFromPosition(
        stateAfterEnemyMove,
        enemyMove.fromPosition
      );

      if (!enemyPiece) {
        continue;
      }

      // CRITICAL CHECK: Make sure enemy isn't moving our pieces (engine bug workaround)
      if (enemyPiece.isLight === ourPiece.isLight) {
        continue;
      }

      try {
        applyMoveWithSnakeRespawn(
          stateAfterEnemyMove,
          enemyMove.fromPosition,
          enemyMove.move,
          enemyPiece
        );
      } catch (error) {
        continue; // Skip invalid enemy moves
      }

      // Switch turn back to us
      stateAfterEnemyMove.isLightTurn = !stateAfterEnemyMove.isLightTurn;

      // FIXED VICTORY LOGIC: Check if we can win after this enemy response
      let canWinAfterThisEnemyMove = false;

      // Find where our king is after enemy response
      let ourKingCurrentPos = null;
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const piece = MCTSUtils_v2.getPieceAt(stateAfterEnemyMove.board, {
            row,
            col,
          });
          if (piece && piece.isKing && piece.isLight === ourPiece.isLight) {
            ourKingCurrentPos = { row, col };
            break;
          }
        }
        if (ourKingCurrentPos) break;
      }

      if (ourKingCurrentPos) {
        // CRITICAL FIX: If our king is already at victory row, we've ALREADY WON
        if (ourKingCurrentPos.row === ourVictoryRow) {
          canWinAfterThisEnemyMove = true;
        } else {
          // Check if our king can reach victory row in one move
          const ourMovesAfterEnemyResponse =
            MCTSMoveGenerationService.getInstance().getAllMoves(
              stateAfterEnemyMove
            );

          for (const ourFinalMove of ourMovesAfterEnemyResponse) {
            const finalMovePiece = MCTSUtils_v2.getPieceAt(
              stateAfterEnemyMove.board,
              ourFinalMove.fromPosition
            );
            if (
              finalMovePiece &&
              finalMovePiece.isKing &&
              finalMovePiece.isLight === ourPiece.isLight
            ) {
              if (ourFinalMove.move.position.row === ourVictoryRow) {
                canWinAfterThisEnemyMove = true;
                break;
              }
            }
          }
        }
      }

      // If we cannot win after this enemy response, the race is blockable
      if (!canWinAfterThisEnemyMove) {
        return {
          canWinInTwoTurns: false,
          description: "Enemy can block",
        };
      }
    }

    // If we can win regardless of enemy response, it's an unstoppable 2-turn race
    return {
      canWinInTwoTurns: true,
      description: "Unstoppable 2-turn race",
    };
  }
}

